import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Heart, Bell, CheckCircle2, Circle, Plus, X, Cake, Wallet, ShieldCheck, Zap,
  Moon, Coffee, Briefcase, Plane, Users as UsersIcon, Utensils, Building2, Sun, Baby, Home as HomeIcon,
  Star, Trash2, ArrowRight, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Presence modes with icon + brand colour. Kept short so the ring picker fits phones.
const MODES = [
  { key: "online",     label: "Online",     icon: Sparkles,  cls: "bg-emerald-500" },
  { key: "work",       label: "Work",       icon: Briefcase, cls: "bg-slate-700" },
  { key: "busy",       label: "Busy",       icon: Zap,       cls: "bg-amber-600" },
  { key: "sleep",      label: "Sleep",      icon: Moon,      cls: "bg-indigo-700" },
  { key: "travelling", label: "Travelling", icon: Plane,     cls: "bg-sky-600" },
  { key: "kids",       label: "Kids",       icon: Baby,      cls: "bg-pink-500" },
  { key: "parents",    label: "Parents",    icon: UsersIcon, cls: "bg-orange-600" },
  { key: "breakfast",  label: "Breakfast",  icon: Coffee,    cls: "bg-yellow-600" },
  { key: "lunch",      label: "Lunch",      icon: Utensils,  cls: "bg-lime-600" },
  { key: "dinner",     label: "Dinner",     icon: Sun,       cls: "bg-orange-500" },
  { key: "restroom",   label: "Restroom",   icon: HomeIcon,  cls: "bg-stone-500" },
  { key: "offline",    label: "Offline",    icon: Circle,    cls: "bg-stone-800" },
];
const MODE_ICON = Object.fromEntries(MODES.map((m) => [m.key, m]));

export default function SmartDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [todoTitle, setTodoTitle] = useState("");
  const [bday, setBday] = useState({ name: "", date: "", relation: "" });

  useSeo({
    title: "Smart Dashboard · find2near",
    description: "Personalised dashboard — recent apps, favourites, AI recommendations, presence, to-do, birthdays and your life-cover policy.",
    canonical: "/dashboard-smart",
  });

  const refresh = () => api.get("/personal/dashboard").then((r) => setD(r.data));

  useEffect(() => {
    if (!user) return;
    refresh().catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Sparkles className="mx-auto text-[#E05E36]" size={44} />
        <h2 className="heading text-2xl font-extrabold mt-4">Sign in to see your dashboard</h2>
        <Link to="/onboarding" className="inline-block mt-6 bg-[#E05E36] text-white px-6 py-3 rounded-full font-bold">Get started</Link>
        {/* Quick account access — still visible signed-out so users can reach
            the notification center + devices to sign back in from a lost
            device flow. Auth is enforced on the target pages. */}
        <div className="max-w-3xl mx-auto grid grid-cols-2 gap-2 mt-8 text-left">
          <Link to="/me/notifications"
                data-testid="me-tile-notifications"
                className="rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-200 p-3 flex items-center gap-2">
            <Bell size={18} className="text-amber-600"/>
            <div>
              <div className="text-sm font-black text-stone-900">Notifications</div>
              <div className="text-[10px] text-stone-500">Unified inbox</div>
            </div>
          </Link>
          <Link to="/me/security/devices"
                data-testid="me-tile-devices"
                className="rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 p-3 flex items-center gap-2">
            <ShieldCheck size={18} className="text-sky-600"/>
            <div>
              <div className="text-sm font-black text-stone-900">My devices</div>
              <div className="text-[10px] text-stone-500">Active sessions</div>
            </div>
          </Link>
        </div>
      </div>
    );
  }
  if (!d) {
    return <div className="p-8 text-center text-stone-500">Loading your dashboard…</div>;
  }

  const setMode = async (mode) => {
    try {
      const r = await api.put("/personal/presence", { mode });
      setD({ ...d, presence: r.data });
    } catch (e) { toast.error("Could not update mode"); }
  };

  const addTodo = async () => {
    if (!todoTitle.trim()) return;
    try {
      await api.post("/personal/todos", { title: todoTitle });
      setTodoTitle("");
      refresh();
    } catch (e) { toast.error("Could not add"); }
  };

  const toggleTodo = async (t) => {
    try {
      await api.patch(`/personal/todos/${t.todo_id}`, { done: !t.done });
      refresh();
    } catch (e) { toast.error("Update failed"); }
  };

  const removeTodo = async (todoId) => {
    try { await api.delete(`/personal/todos/${todoId}`); refresh(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const addBday = async () => {
    if (!bday.name.trim() || !bday.date.trim()) return;
    try {
      await api.post("/personal/birthdays", bday);
      setBday({ name: "", date: "", relation: "" });
      refresh();
      toast.success("Birthday saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const clearNotif = async (id) => {
    try { await api.patch(`/personal/notifications/${id}`); refresh(); }
    catch (e) {}
  };

  const currentMode = MODE_ICON[d.presence.mode] || MODE_ICON.online;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="smart-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Smart Dashboard</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
            Hi, <span className="text-[#E05E36]">{user.name?.split(" ")[0] || "friend"}</span>
          </h1>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full ${currentMode.cls} text-white text-xs font-black px-3 py-1.5 shadow-lg`} data-testid="dash-presence-badge">
          <currentMode.icon size={14} /> {currentMode.label}
        </div>
      </div>

      {/* Presence ring picker */}
      <div className="rounded-3xl bg-white border border-stone-200 p-4 md:p-5">
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-3">Set your mode</div>
        <div className="flex flex-wrap gap-2" data-testid="dash-modes">
          {MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-3 py-1.5 transition-colors ${
                d.presence.mode === m.key ? `${m.cls} text-white` : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
              data-testid={`dash-mode-${m.key}`}
            >
              <m.icon size={13} /> {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid: Insurance + To-Do + Birthdays */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Insurance card */}
        <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-800 text-white p-5 relative overflow-hidden" data-testid="dash-policy">
          <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="text-[10px] uppercase tracking-widest text-white/70 font-black flex items-center gap-1">
            <ShieldCheck size={12} /> Complimentary cover
          </div>
          <div className="heading text-3xl font-black mt-2 tabular-nums">{rupees(d.policy.cover_inr)}</div>
          <div className="text-xs text-white/70">Accidental life cover · {rupees(d.policy.premium_inr)} / yr</div>
          <div className="mt-3 text-[11px] text-white/70">
            Active until {new Date(d.policy.expires_at).toLocaleDateString()} · Beneficiary: {d.policy.beneficiary}
          </div>
          <div className="mt-3 rounded-lg bg-white/15 px-2 py-1 text-[10px] font-mono">{d.policy.policy_id}</div>
        </div>

        {/* To-Do */}
        <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="dash-todos">
          <div className="flex items-center justify-between mb-3">
            <div className="heading text-base font-black text-stone-900 flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-600" /> To-do
            </div>
            <span className="text-[10px] text-stone-400 font-bold">{d.todos_open_count} open</span>
          </div>
          <div className="flex items-center gap-1 border border-stone-200 rounded-full pl-3 pr-1 py-1 mb-3">
            <input
              value={todoTitle}
              onChange={(e) => setTodoTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTodo()}
              placeholder="Buy milk, call mom…"
              className="flex-1 text-sm bg-transparent outline-none"
              data-testid="dash-todo-input"
            />
            <button
              onClick={addTodo}
              className="w-7 h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white grid place-items-center"
              data-testid="dash-todo-add"
            >
              <Plus size={14} />
            </button>
          </div>
          {(d.todos_pending || []).length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-4">Nothing pending. Enjoy your day.</div>
          ) : (
            <ul className="space-y-1.5">
              {d.todos_pending.map((t) => (
                <li key={t.todo_id} className="flex items-center gap-2 text-sm" data-testid={`dash-todo-${t.todo_id}`}>
                  <button onClick={() => toggleTodo(t)} className="text-emerald-600" title="Mark done">
                    <Circle size={14} />
                  </button>
                  <span className="flex-1 truncate text-stone-800">{t.title}</span>
                  <button onClick={() => removeTodo(t.todo_id)} className="text-stone-400 hover:text-red-600">
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Birthdays */}
        <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="dash-birthdays">
          <div className="flex items-center justify-between mb-3">
            <div className="heading text-base font-black text-stone-900 flex items-center gap-2">
              <Cake size={16} className="text-pink-500" /> Birthdays
            </div>
            <span className="text-[10px] text-stone-400 font-bold">This week</span>
          </div>
          {(d.upcoming_bdays || []).length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-2">No birthdays in the next 7 days.</div>
          ) : (
            <ul className="space-y-1.5 mb-3">
              {d.upcoming_bdays.map((b) => (
                <li key={b.birthday_id} className="flex items-center gap-2 text-sm" data-testid={`dash-bday-${b.birthday_id}`}>
                  <span className={`w-8 h-8 rounded-full grid place-items-center text-[10px] font-black ${b.date === d.today_mmdd ? "bg-pink-500 text-white" : "bg-pink-100 text-pink-700"}`}>
                    {b.date}
                  </span>
                  <span className="flex-1 truncate text-stone-800">{b.name}</span>
                  <span className="text-[10px] text-stone-500">{b.relation || ""}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-3 gap-1 text-xs">
            <input
              value={bday.name}
              onChange={(e) => setBday({ ...bday, name: e.target.value })}
              placeholder="Name"
              className="px-2 py-1.5 rounded border border-stone-200 outline-none"
              data-testid="dash-bday-name"
            />
            <input
              value={bday.date}
              onChange={(e) => setBday({ ...bday, date: e.target.value })}
              placeholder="MM-DD"
              className="px-2 py-1.5 rounded border border-stone-200 outline-none font-mono"
              data-testid="dash-bday-date"
            />
            <button
              onClick={addBday}
              className="rounded bg-pink-500 hover:bg-pink-600 text-white font-bold"
              data-testid="dash-bday-add"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Quick account access — Notification Center + My Devices (P3/P4) */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="dash-quick-access">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-black mb-3">Account</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Link to="/me/notifications"
                data-testid="me-tile-notifications"
                className="rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-200 p-3 flex items-center gap-2 transition-colors">
            <Bell size={18} className="text-amber-600"/>
            <div>
              <div className="text-sm font-black text-stone-900">Notifications</div>
              <div className="text-[10px] text-stone-500">Unified inbox</div>
            </div>
          </Link>
          <Link to="/me/security/devices"
                data-testid="me-tile-devices"
                className="rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 p-3 flex items-center gap-2 transition-colors">
            <ShieldCheck size={18} className="text-sky-600"/>
            <div>
              <div className="text-sm font-black text-stone-900">My devices</div>
              <div className="text-[10px] text-stone-500">Active sessions</div>
            </div>
          </Link>
          <Link to="/two-factor"
                data-testid="me-tile-2fa"
                className="rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 p-3 flex items-center gap-2 transition-colors">
            <ShieldCheck size={18} className="text-emerald-600"/>
            <div>
              <div className="text-sm font-black text-stone-900">2-step</div>
              <div className="text-[10px] text-stone-500">TOTP MFA</div>
            </div>
          </Link>
          <Link to="/wallet"
                data-testid="me-tile-wallet"
                className="rounded-xl bg-stone-50 hover:bg-stone-100 border border-stone-200 p-3 flex items-center gap-2 transition-colors">
            <Wallet size={18} className="text-stone-700"/>
            <div>
              <div className="text-sm font-black text-stone-900">Wallet</div>
              <div className="text-[10px] text-stone-500">Balance & passes</div>
            </div>
          </Link>
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5 md:p-6" data-testid="dash-ai-recs">
        <div className="flex items-center justify-between mb-3">
          <div className="heading text-lg font-black text-stone-900 flex items-center gap-2">
            <Sparkles size={16} className="text-fuchsia-600" /> AI recommendations
          </div>
          <Link to="/apps" className="text-xs text-fuchsia-700 font-bold inline-flex items-center gap-1">All apps <ChevronRight size={12} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {d.ai_recommendations.map((r) => (
            <Link
              key={r.module}
              to={`/${r.module}`}
              className="rounded-xl bg-fuchsia-50 hover:bg-fuchsia-100 border border-fuchsia-200 p-3 transition-colors"
              data-testid={`dash-rec-${r.module.replace(/[^a-z0-9]/gi, "-")}`}
            >
              <div className="text-sm font-black text-stone-900">{r.label}</div>
              <div className="text-[11px] text-stone-500">{r.reason}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent + Favorites */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="dash-recent">
          <div className="heading text-base font-black text-stone-900 mb-3">Recent</div>
          {(d.recent || []).length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-4">Your recent modules will appear here.</div>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {d.recent.map((r) => (
                <Link
                  key={r.module}
                  to={`/${r.module}`}
                  className="rounded-lg bg-stone-50 hover:bg-stone-100 px-3 py-2 text-sm font-semibold text-stone-800 flex items-center justify-between"
                  data-testid={`dash-recent-${r.module}`}
                >
                  <span className="truncate">{r.label}</span>
                  <ArrowRight size={12} className="text-stone-400" />
                </Link>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="dash-favorites">
          <div className="heading text-base font-black text-stone-900 mb-3 flex items-center gap-2">
            <Star size={16} className="text-amber-500" /> Favourites
          </div>
          {(d.favorites || []).length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-4">Pin any module from AI recommendations or your recent list.</div>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {d.favorites.map((f) => (
                <Link
                  key={f.module}
                  to={`/${f.module}`}
                  className="rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-2 text-sm font-semibold text-stone-800 flex items-center justify-between"
                  data-testid={`dash-fav-${f.module}`}
                >
                  <span className="truncate">{f.label}</span>
                  <ArrowRight size={12} className="text-stone-400" />
                </Link>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Notifications */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="dash-notifications">
        <div className="flex items-center justify-between mb-3">
          <div className="heading text-base font-black text-stone-900 flex items-center gap-2">
            <Bell size={16} className="text-amber-600" /> Notifications
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-stone-400 font-bold">{d.notifications_unread_count} unread</span>
            <Link to="/notifications" className="text-[11px] font-black text-[#E05E36] hover:underline"
                  data-testid="dash-notifications-open-inbox">
              Open inbox →
            </Link>
          </div>
        </div>
        {(d.notifications || []).length === 0 ? (
          <div className="text-xs text-stone-400 text-center py-4">You're all caught up.</div>
        ) : (
          <ul className="space-y-2">
            {d.notifications.map((n) => (
              <li key={n.notif_id} className="flex items-center gap-3 text-sm" data-testid={`dash-notif-${n.notif_id}`}>
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-stone-500 truncate">{n.body}</div>}
                </div>
                {n.link && (
                  <Link to={n.link} className="text-xs text-[#E05E36] font-bold">Open</Link>
                )}
                <button onClick={() => clearNotif(n.notif_id)} className="text-stone-400 hover:text-stone-700">
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
